import { useMemo, useState } from "react";
import { Loader2 } from "lucide-react";
import { motion } from "motion/react";

import { type Conversation } from "shared/apiClient";
import { getText } from "shared/lib";
import {
  newRemovingOrPruningTreeDescriptor,
  type TreeDescriptor,
} from "@/components/treeModule/lib";
import { animationVariants } from "@/components/wizard/widgets/lib";
import { isTreeItemValid } from "@/components/treeModule/treeList/lib";

import { useCouncilLocalisation } from "shared/hooks";
import { type TreeModuleExpertResult } from "@/hooks";

import { Button } from "shared/components";
import {
  MultipleChoiceQuestion,
  RemovingOrPruningTreeList,
} from "@/components/treeModule";
import { PropertyBoundariesDisclaimer } from "./PropertyBounderiesDislcaimer";

export type RemovingPruningTreesTreesData = {
  areYouRemovingOrPruning: string;
  treeDescriptors: Array<TreeDescriptor>;
};

export interface RemovingPruningTreesContainerProps {
  conversation: Conversation | null | undefined;
  disabled?: boolean;
  nextButtonLabel?: string;
  onClickNext: (treeData: RemovingPruningTreesTreesData) => void;
  showLoading?: boolean;
  state: string;
  submitting?: boolean;
  treeModuleExpertResult: TreeModuleExpertResult | undefined | null;
}

export function RemovingPruningTreesContainer({
  conversation,
  disabled,
  nextButtonLabel,
  onClickNext,
  showLoading,
  state,
  submitting,
  treeModuleExpertResult,
}: RemovingPruningTreesContainerProps) {
  const { getLocalisationEntry } = useCouncilLocalisation(conversation);

  const [areYouRemovingOrPruning, setAreYouRemovingOrPruning] = useState("");

  const newTreeOptions = useMemo(() => {
    return {
      distanceAboveGroundLevel:
        treeModuleExpertResult?.distance_above_ground_level.toString(),
      showTreeHeight: state.toUpperCase() === "VIC",
      treeHeight: treeModuleExpertResult?.tree_height_trigger,
      useCircumference:
        treeModuleExpertResult?.measurement_method === "circumference",
    };
  }, [state, treeModuleExpertResult]);

  const [treeDescriptors, setTreeDescriptors] = useState<Array<TreeDescriptor>>(
    [],
  );

  const [treesHaveBeenDeleted, setTreesHaveBeenDeleted] = useState(false);

  const isValid = useMemo(() => {
    if (areYouRemovingOrPruning === "no") return true;

    if (areYouRemovingOrPruning === "") return true;

    if (areYouRemovingOrPruning === "yes" && treeDescriptors.length === 0) {
      return false;
    }

    if (treeDescriptors.length === 0) return true;

    return treeDescriptors.every((treeDescriptor) =>
      isTreeItemValid(treeDescriptor),
    );
  }, [areYouRemovingOrPruning, treeDescriptors]);

  const renderFooter = () => {
    return (
      <motion.div
        variants={animationVariants}
        animate={["buttons"]}
        className="mt-3 flex justify-end md:mt-8"
      >
        <Button
          data-testid="next-button"
          disabled={submitting || showLoading || disabled || !isValid}
          onClick={() =>
            onClickNext({
              areYouRemovingOrPruning,
              treeDescriptors,
            })
          }
        >
          {submitting || showLoading ? (
            <>
              <Loader2 className="mr-3 animate-spin" data-testid="submitting" />
            </>
          ) : null}
          {getText(nextButtonLabel || "Continue")}
        </Button>
      </motion.div>
    );
  };

  return (
    <div
      className="wizard-step-transition flex h-full w-full flex-col gap-5 md:w-[600px] md:max-w-[600px]"
      data-testid="step-container-removing-pruning-trees"
    >
      <MultipleChoiceQuestion
        choices={[
          { label: getText("Yes"), value: "yes" },
          { label: getText("No"), value: "no" },
          { label: getText("Not sure yet"), value: "unsure" },
        ]}
        onChange={setAreYouRemovingOrPruning}
        title={getText("Are you removing or pruning any trees or vegetation?")}
        value={areYouRemovingOrPruning}
      />

      {["yes", "unsure"].includes(areYouRemovingOrPruning) && (
        <>
          <RemovingOrPruningTreeList
            hideDelete={
              areYouRemovingOrPruning === "unsure"
                ? false
                : treeDescriptors.length < 2
            }
            onChangeTreeDescriptors={setTreeDescriptors}
            onTreeDeleted={() => setTreesHaveBeenDeleted(true)}
            newTreeOptions={newTreeOptions}
            subTitle={getText(
              "This helps us determine if the trees or vegetation would require a permit to remove or prune.",
            )}
            title={getText(
              areYouRemovingOrPruning === "yes"
                ? "Describe the trees/vegetation"
                : "If you know the details, describe the trees/vegetation you might remove or prune",
            )}
            treeDescriptors={treeDescriptors}
            treesHaveBeenDeleted={treesHaveBeenDeleted}
          />
        </>
      )}

      <PropertyBoundariesDisclaimer
        contactCouncilUrl={getLocalisationEntry("councilContactLink")}
        isRemoving={["yes", "unsure"].includes(areYouRemovingOrPruning)}
      />

      {renderFooter()}
    </div>
  );
}
